import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import CfrPopover from "@/components/CfrPopover";

// Matches: "21 CFR 820", "21 CFR 820.30", "21 CFR 820.30(j)", "ISO 14971", "IEC 62304", "IEC 62366", "ISO 10993".
const CFR_REGEX =
  /(21 CFR \d+(?:\.\d+(?:\([a-z0-9]+\))?)?|ISO \d{4,5}(?:-\d+)?|IEC \d{4,5}(?:-\d+)?)/g;

function tokenize(text) {
  // Split a plain string into [string, {ref:string}, string, ...]
  if (typeof text !== "string") return [text];
  const parts = [];
  let last = 0;
  let m;
  CFR_REGEX.lastIndex = 0;
  while ((m = CFR_REGEX.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push({ ref: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Recursively walk React children; replace CFR strings with <CfrPopover>. */
function decorate(children) {
  return React.Children.map(children, (child, idx) => {
    if (typeof child === "string") {
      const parts = tokenize(child);
      if (parts.length === 1 && typeof parts[0] === "string") return child;
      return parts.map((p, i) =>
        typeof p === "string"
          ? p
          : <CfrPopover key={`${idx}-${i}`} reg={p.ref} />
      );
    }
    if (React.isValidElement(child)) {
      // Don't traverse into <code> / <pre> — regulations there are usually code snippets we want left alone.
      if (child.type === "code" || child.type === "pre") return child;
      if (child.props?.children) {
        return React.cloneElement(child, {
          children: decorate(child.props.children),
        });
      }
    }
    return child;
  });
}

const WrapCfr = ({ children }) => <>{decorate(children)}</>;

// Custom renderers: run text nodes through decorate()
const components = {
  p:  ({ children }) => <p><WrapCfr>{children}</WrapCfr></p>,
  li: ({ children }) => <li><WrapCfr>{children}</WrapCfr></li>,
  td: ({ children }) => <td><WrapCfr>{children}</WrapCfr></td>,
  strong: ({ children }) => <strong><WrapCfr>{children}</WrapCfr></strong>,
  em: ({ children }) => <em><WrapCfr>{children}</WrapCfr></em>,
};

export default function Markdown({ children, className = "" }) {
  return (
    <div className={`md-body ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
